import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

const VOLUME_TIERS = [
  { label: '1 – 49 records',      price: '$1.00 / rec' },
  { label: '50 – 199 records',    price: '$0.75 / rec' },
  { label: '200 – 999 records',   price: '$0.50 / rec' },
  { label: '1,000 + records',     price: '$0.30 / rec' },
];

const tiers = [
  {
    title: 'Free',
    priceDisplay: '$0',
    priceUnit: 'forever',
    description: [
      '5 cloud records / month',
      'Unlimited local via Claude Code plugin',
      'CSV & JSON export',
      'Community support',
    ],
    buttonText: 'Get free API key',
    buttonVariant: 'outlined',
    buttonHref: '/signup',
    featured: false,
  },
  {
    title: 'Credits',
    subheader: 'Pay as you go',
    priceDisplay: '$1.00',
    priceUnit: 'per record',
    description: [
      'Credits never expire',
      'All export formats — CSV, JSON, FHIR R4',
      'S3 and FHIR server push',
      'Schema upload',
      'Priority email support',
      'HIPAA-compliant generation',
    ],
    buttonText: 'Buy credits',
    buttonVariant: 'contained',
    buttonHref: '/signup',
    featured: true,
    showVolume: true,
  },
  {
    title: 'Enterprise',
    priceDisplay: 'Custom',
    priceUnit: null,
    description: [
      'Dedicated infrastructure',
      'Custom data connectors',
      'SLA & compliance support',
      'On-premise deployment option',
      'Dedicated account manager',
      'Bulk pricing negotiated',
    ],
    buttonText: 'Contact sales',
    buttonVariant: 'outlined',
    buttonHref: 'mailto:sales@bacclabs.io',
    featured: false,
  },
];

export default function Pricing() {
  return (
    <Container
      id="pricing"
      sx={{
        pt: { xs: 4, sm: 12 },
        pb: { xs: 8, sm: 16 },
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 3, sm: 6 },
      }}
    >
      <Box sx={{ width: { sm: '100%', md: '60%' }, textAlign: { sm: 'left', md: 'center' } }}>
        <Typography component="h2" variant="h4" color="text.primary">
          Pricing
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Pay only for what you generate. Credits never expire — buy more when you need them.
        </Typography>
      </Box>

      <Grid container spacing={3} alignItems="stretch" justifyContent="center">
        {tiers.map((tier) => (
          <Grid
            item
            key={tier.title}
            xs={12}
            sm={tier.title === 'Enterprise' ? 12 : 6}
            md={4}
          >
            <Card
              sx={{
                p: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                border: tier.featured ? '1px solid' : undefined,
                borderColor: tier.featured ? 'primary.main' : undefined,
                background: tier.featured
                  ? 'linear-gradient(#033363, #021F3B)'
                  : undefined,
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                {/* Title row */}
                <Box
                  sx={{
                    mb: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: tier.featured ? 'grey.100' : '',
                  }}
                >
                  <Typography component="h3" variant="h6">
                    {tier.title}
                  </Typography>
                  {tier.featured && (
                    <Chip
                      icon={<AutoAwesomeIcon />}
                      label={tier.subheader}
                      size="small"
                      sx={{
                        background: (theme) =>
                          theme.palette.mode === 'light' ? '' : 'none',
                        backgroundColor: 'primary.contrastText',
                        '& .MuiChip-label': { color: 'primary.dark' },
                        '& .MuiChip-icon': { color: 'primary.dark' },
                      }}
                    />
                  )}
                </Box>

                {/* Price */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 0.5,
                    color: tier.featured ? 'grey.50' : undefined,
                  }}
                >
                  <Typography component="h3" variant="h2">
                    {tier.priceDisplay}
                  </Typography>
                  {tier.priceUnit && (
                    <Typography component="span" variant="h6" sx={{ opacity: 0.6 }}>
                      {tier.priceUnit}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 2, opacity: 0.2, borderColor: 'grey.500' }} />

                {/* Features */}
                {tier.description.map((line) => (
                  <Box
                    key={line}
                    sx={{ py: 0.75, display: 'flex', gap: 1.5, alignItems: 'center' }}
                  >
                    <CheckCircleRoundedIcon
                      sx={{
                        width: 20,
                        color: tier.featured ? 'primary.light' : 'primary.main',
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      component="span"
                      variant="subtitle2"
                      sx={{ color: tier.featured ? 'grey.200' : undefined }}
                    >
                      {line}
                    </Typography>
                  </Box>
                ))}

                {/* Volume discount table — Credits card only */}
                {tier.showVolume && (
                  <Box
                    sx={{
                      mt: 2.5,
                      p: 1.5,
                      borderRadius: 2,
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: 'grey.400', fontWeight: 700, letterSpacing: '0.07em', display: 'block', mb: 1 }}
                    >
                      VOLUME DISCOUNTS
                    </Typography>
                    {VOLUME_TIERS.map((vt) => (
                      <Box
                        key={vt.label}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 0.4,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'grey.300' }}>
                          {vt.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'primary.light', fontWeight: 700 }}>
                          {vt.price}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>

              <CardActions>
                <Button
                  fullWidth
                  variant={tier.buttonVariant}
                  component="a"
                  href={tier.buttonHref}
                >
                  {tier.buttonText}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: -2 }}>
        All plans include HIPAA-compliant generation. Credits are pre-purchased and never expire.
      </Typography>
    </Container>
  );
}
